/**
 * useElasticsearchOptions
 * --------------------------------------------------------------------------
 * Single source of truth for "fetch options for a select dropdown". Handles
 * BOTH the ES aggregation endpoint and the REST API endpoint branch (e.g.
 * the Culture tree API). The component does not care which served the
 * response — both paths return the same `{ options, page, hasMore }` shape.
 *
 * Why this hook exists (the bug it kills)
 * --------------------------------------------------------------------------
 * The previous implementation manually managed `options`, `currentPage`,
 * `hasMore`, `searchQuery`, and a chain of useEffects that all called a
 * single `fetchResults` closure. There were two race-condition paths that
 * silently corrupted the dropdown state:
 *
 *   1. No request cancellation. Typing "Py" then "Pyt" fired two requests
 *      in parallel; if the first returned later, its results overwrote the
 *      second's via `setOptions(formattedOptions)`. Result: user types
 *      "Python", waits 1–2 s, dropdown reverts to the unfiltered list.
 *
 *   2. Effect-driven refetches with stale closures. The `activeFacets`
 *      effect read `searchQuery` (debounced state) at the time *the effect
 *      ran*, not the time the user typed. A parent re-render during the
 *      150 ms debounce window with a new object reference for `filters`
 *      or `apiFilters` caused `fetchResults` to be a new closure → effect
 *      fired with `searchQuery=""`, clobbering the filtered list.
 *
 * How RQ fixes both
 * --------------------------------------------------------------------------
 *  - `useInfiniteQuery` deep-compares the query key. Reference-unstable
 *    objects (`filters`, `facets`, `searchFields`, …) do NOT cause refetches
 *    as long as their *contents* are equal.
 *  - `placeholderData: keepPreviousData` keeps the last successful options
 *    visible while the next query loads — no flicker on each keystroke.
 *  - `AbortSignal` is threaded through to axios. When the query key changes
 *    (e.g. `query: "Py"` → `query: "Pyt"`), RQ aborts the prior request.
 *    That's the ONLY thing that prevents an old "Py" response from landing
 *    after the new "Pyt" one. The signal-aware axios interceptor in
 *    services/api.js suppresses the cancel error toast.
 *
 * Note on cache busting
 * --------------------------------------------------------------------------
 * axios appends `_t=<timestamp>` to GET params via a request interceptor.
 * RQ caches by query key, axios cache-busts at the network layer — they
 * don't fight, this is intentional.
 */

import { useMemo } from 'react';
import { useInfiniteQuery, useQuery, keepPreviousData } from '@tanstack/react-query';
import { ElasticsearchService } from '@client/services/elasticsearchService';
import axiosInstance from '../../services/api';

export const elasticsearchOptionsKeys = {
  all: ['elasticsearch-options'],
  query: (params) => ['elasticsearch-options', params],
};

/**
 * Drop this dropdown's OWN field from the cross-field facet set.
 *
 * Why this matters (R11, 27 mai 26): `activeFacets` is built from EVERY
 * filter, including this one. When the user picks an option here, the parent
 * adds this filter's value to `activeFacets`; if that change reached the
 * query key, the infinite query would refetch from page 1 — resetting the
 * dropdown scroll to the top and discarding the pages the user already
 * scrolled through. The backend already excludes the self-field from its
 * aggregation, so sending it changes nothing in the response; the only
 * effect is the wasteful refetch. Excluding it here keeps the key stable
 * across self-selection, so the list stays exactly where the user left it
 * (matching native AntD behaviour).
 *
 * The self facet path mirrors FilterSection.buildActiveFacets:
 *   value_field set  -> `${field}.${value_field}`
 *   otherwise        -> `${field}`
 */
function excludeSelfFacet(facets, field, valueField) {
  if (!facets || typeof facets !== 'object') return facets;
  const selfKey = valueField ? `${field}.${valueField}` : field;
  if (!(selfKey in facets)) return facets;
  const rest = { ...facets };
  delete rest[selfKey];
  return rest;
}

export function useElasticsearchOptions({
  document,
  field,
  query = '',
  pageSize = 20,
  sortBy = 'count',
  sortOrder = 'desc',
  valueField = null,
  labelField = null,
  filters = null,
  facets = null,
  searchFields = null,
  includeSourceFields = null,
  apiEndpoint = null,
  apiFilters = null,
  enabled = true,
}) {
  // Strip this dropdown's own selection from the facet set so picking an
  // option here doesn't invalidate its own query (see excludeSelfFacet).
  const effectiveFacets = useMemo(
    () => excludeSelfFacet(facets, field, valueField),
    [facets, field, valueField],
  );

  // The key includes EVERY input that affects the response. RQ deep-compares
  // so unstable references are fine; what matters is value equality.
  const queryKey = elasticsearchOptionsKeys.query({
    document,
    field,
    query,
    pageSize,
    sortBy,
    sortOrder,
    valueField,
    labelField,
    filters,
    facets: effectiveFacets,
    searchFields,
    includeSourceFields,
    apiEndpoint,
    apiFilters,
  });

  return useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam = 1, signal }) =>
      apiEndpoint
        ? fetchFromApiEndpoint({
            apiEndpoint,
            apiFilters,
            query,
            valueField,
            labelField,
            signal,
          })
        : fetchFromElasticsearch({
            document,
            field,
            query,
            page: pageParam,
            pageSize,
            sortBy,
            sortOrder,
            valueField,
            labelField,
            filters,
            facets: effectiveFacets,
            searchFields,
            includeSourceFields,
            signal,
          }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.hasMore ? lastPage.page + 1 : undefined,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    enabled: enabled && (!!apiEndpoint || (!!document && !!field)),
  });
}

/**
 * Resolve the labels of already-SELECTED option values in the CURRENT label
 * field (language / display-mode), independent of pagination.
 *
 * Why this exists: a big filter (e.g. Ennemis — 1000+ options) only loads the
 * first page, so a selected value sits far down the list and never appears in
 * the fetched options. Its tag label then falls back to the stale label
 * captured at selection time and does NOT follow a language / Type-d'affichage
 * change (R82/R85). We re-fetch each selected value by id (filter_<valueField>)
 * with the current labelField so the tag always shows the current-language
 * name. Small filters (Cultures — 8 options) never hit this because the
 * selection is already in the loaded page; we only resolve the ids that are
 * missing from the current options.
 */
export function useSelectedOptionLabels({
  document,
  field,
  valueField,
  labelField,
  ids,
  baseFilters,
  enabled = true,
}) {
  const sortedIds = Array.isArray(ids)
    ? [...ids].filter(Boolean).map(String).sort()
    : [];

  return useQuery({
    queryKey: [
      'selected-option-labels',
      document,
      field,
      labelField,
      sortedIds,
      baseFilters,
    ],
    enabled:
      enabled
      && sortedIds.length > 0
      && !!document
      && !!field
      && !!valueField
      && !!labelField,
    staleTime: 60_000,
    queryFn: async ({ signal }) => {
      // eslint-disable-next-line no-undef
      const entries = await Promise.all(
        sortedIds.map(async (id) => {
          const res = await ElasticsearchService.search(
            {
              document,
              field,
              value_field: valueField,
              label_field: labelField,
              size: 1,
              sort: 'natural',
              ...(baseFilters || {}),
              [`filter_${valueField}`]: id,
            },
            { signal },
          );
          const opt = res?.options?.[0];
          return opt ? [id, opt.label] : null;
        }),
      );
      const map = {};
      entries.filter(Boolean).forEach(([id, label]) => {
        map[id] = label;
      });
      return map;
    },
  });
}

// ----------------------------------------------------------------------------
// Internal fetchers
// ----------------------------------------------------------------------------

async function fetchFromApiEndpoint({
  apiEndpoint,
  apiFilters,
  query,
  valueField,
  labelField,
  signal,
}) {
  const { data } = await axiosInstance.get(apiEndpoint, {
    params: { search: query || '', ...apiFilters },
    signal,
  });
  const results = data?.results || data?.data || [];
  return {
    options: results.map((item) => ({
      label: item?.[labelField] ?? item?.french_name,
      value: item?.[valueField] ?? item?.id,
      count: null,
      highlighted: false,
      source: null,
    })),
    page: 1,
    hasMore: false,
  };
}

async function fetchFromElasticsearch({
  document,
  field,
  query,
  page,
  pageSize,
  sortBy,
  sortOrder,
  valueField,
  labelField,
  filters,
  facets,
  searchFields,
  includeSourceFields,
  signal,
}) {
  // facets → backend `facet_<field>=v1|v2`. Pipe separator, NOT comma:
  // facet values can be product names like "2,4-D AMINE 500 (Corteva)" —
  // a comma join would split them into garbage values server-side. Matches
  // the main search endpoint's multi-value separator (BaseFilter).
  const facetParams = {};
  if (facets && typeof facets === 'object') {
    Object.entries(facets).forEach(([facetField, vals]) => {
      if (!Array.isArray(vals) || vals.length === 0) return;
      facetParams[`facet_${facetField}`] = vals.join('|');
    });
  }

  // multi-field text search & extra _source paths
  const extraSearchParams = {};
  if (Array.isArray(searchFields) && searchFields.length > 0) {
    extraSearchParams.search_fields = searchFields
      .map((sf) => (typeof sf === 'string' ? sf : sf.field))
      .join(',');
  }
  if (Array.isArray(includeSourceFields) && includeSourceFields.length > 0) {
    extraSearchParams.include_source_fields = includeSourceFields.join(',');
  }

  const response = await ElasticsearchService.search(
    {
      document,
      field,
      q: query || '',
      page,
      size: pageSize,
      sort: sortBy,
      order: sortOrder,
      value_field: valueField,
      label_field: labelField,
      ...filters,
      ...facetParams,
      ...extraSearchParams,
    },
    { signal },
  );

  return {
    options: (response?.options || []).map((opt) => ({
      label: opt.label,
      value: opt.value,
      count: opt.count,
      highlighted: opt.highlighted,
      source: opt.source,
    })),
    page,
    hasMore: !!response?.pagination?.has_more,
  };
}